"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Container = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const Provider_1 = require("./Provider");
const style_module_scss_1 = __importDefault(require("./style.module.scss"));
const html_viewport_1 = __importDefault(require("html-viewport"));
const helpers_1 = require("./helpers");
const Modal_1 = __importDefault(require("./Components/Modal"));
const BottomSheet_1 = __importDefault(require("./Components/BottomSheet"));
const Popup_1 = __importDefault(require("./Components/Popup"));
const Context_1 = require("./Context");
const Container = (0, react_1.forwardRef)((props, ref) => {
    const { bottomSheet, name, disableClose = false, backgroundClose = true, bottomSheetMaxWidth = 550, popup, theme = 'light', effect = 'scale', onHide } = props;
    const containerRef = (0, react_1.useRef)(null);
    const footerRef = (0, react_1.useRef)(null);
    const showStatus = (0, react_1.useRef)(false);
    const initPosition = (0, react_1.useRef)();
    const [isShow, setShow] = (0, react_1.useState)(false);
    const [isMobile, setMobile] = (0, react_1.useState)(false);
    const [footer, setFooter] = (0, react_1.useState)();
    const providerContext = (0, react_1.useContext)(Provider_1.ProviderContext);
    const show = () => {
        setShow(true);
    };
    const showPopup = (event) => {
        if (event && 'target' in event) {
            initPosition.current = event.target.getBoundingClientRect();
        }
        show();
    };
    const _hide = () => {
        const element = containerRef.current;
        if (!element)
            return;
        element.ontransitionend = () => {
            setShow(false);
        };
        element === null || element === void 0 ? void 0 : element.classList.remove(style_module_scss_1.default['active']);
    };
    const hide = () => {
        _hide();
    };
    const modalHide = () => {
        if (disableClose)
            return;
        _hide();
    };
    const backgroundHide = () => {
        if (!backgroundClose)
            return;
        modalHide();
    };
    const blockScroll = (status) => {
        if (!document.body)
            return;
        const classBody = document.body.classList;
        status ? classBody.add(style_module_scss_1.default['block-scroll']) : classBody.remove(style_module_scss_1.default['block-scroll']);
    };
    const handleEscape = (e) => {
        if (e.code === 'Escape') {
            hide();
        }
    };
    const resetFocus = (e) => {
        if (!(e.relatedTarget && e.relatedTarget.closest('.' + style_module_scss_1.default['modal-container']))) {
            e.currentTarget.focus();
        }
    };
    const handleSize = (e) => {
        const width = e ? e.target.innerWidth : window.innerWidth;
        const isCurrentMobile = width <= bottomSheetMaxWidth;
        if (isCurrentMobile !== isMobile)
            setMobile(isCurrentMobile);
    };
    (0, react_1.useEffect)(() => {
        showStatus.current = isShow;
        const viewport = new html_viewport_1.default;
        if (isShow) {
            viewport.add('interactive-widget', 'resizes-content');
            requestAnimationFrame(() => { var _a; return (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.classList.add(style_module_scss_1.default['active']); });
        }
        else {
            viewport.remove('interactive-widget');
            onHide === null || onHide === void 0 ? void 0 : onHide();
        }
        requestAnimationFrame(() => {
            if (!isShow && providerContext.count() > 0)
                return;
            blockScroll(isShow);
        });
    }, [isShow]);
    (0, react_1.useEffect)(() => {
        return () => {
            showStatus.current = false;
            blockScroll(false);
        };
    }, []);
    (0, react_1.useLayoutEffect)(() => {
        handleSize();
        window.addEventListener('resize', handleSize);
        return () => {
            window.removeEventListener('resize', handleSize);
        };
    }, [isMobile, bottomSheetMaxWidth]);
    /**
     * Unset background cursor pointer
     */
    (0, react_1.useLayoutEffect)(() => {
        if (!containerRef.current)
            return;
        const container = containerRef.current;
        if (backgroundClose)
            container === null || container === void 0 ? void 0 : container.classList.remove(style_module_scss_1.default['cursor-unset']);
        else
            container === null || container === void 0 ? void 0 : container.classList.add(style_module_scss_1.default['cursor-unset']);
    }, [backgroundClose]);
    (0, react_1.useLayoutEffect)(() => {
        providerContext.push(name, {
            show,
            hide: _hide,
            showStatus
        });
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);
    (0, react_1.useImperativeHandle)(ref, () => ({
        show,
        showPopup,
        hide,
        footerRef
    }));
    const classesRoot = (0, react_1.useMemo)(() => {
        const classesRoot = [
            (0, helpers_1.Style)('container')
        ];
        if (theme)
            classesRoot.push(theme);
        if (effect)
            classesRoot.push(style_module_scss_1.default['effect-' + effect]);
        if (bottomSheet)
            classesRoot.push(style_module_scss_1.default['bottomsheet']);
        return classesRoot.join(' ');
    }, [theme, effect, bottomSheet]);
    if (!providerContext.modals || !providerContext.modals.current) {
        return (null);
    }
    const renderProps = Object.assign(Object.assign({}, props), { showPopup,
        show,
        hide,
        footerRef });
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsx)(Context_1.ContainerContext.Provider, { value: {
            initPosition,
            footer,
            setFooter,
            hide
        }, children: isShow &&
            (0, jsx_runtime_1.jsx)("div", { className: classesRoot, ref: containerRef, children: (bottomSheet && isMobile) ?
                    (0, jsx_runtime_1.jsx)(BottomSheet_1.default, Object.assign({}, props, { onBackground: backgroundHide, onClose: modalHide, renderProps: renderProps })) : (popup ? (0, jsx_runtime_1.jsx)(Popup_1.default, Object.assign({}, props, { onBackground: backgroundHide, onClose: modalHide, renderProps: renderProps })) : (0, jsx_runtime_1.jsx)(Modal_1.default, Object.assign({}, props, { onBackground: backgroundHide, onClose: modalHide, renderProps: renderProps }))) }) }), providerContext.modals.current);
});
exports.Container = Container;
